// Console de chat: Global (todos os jogadores online), Server Log, Loot, Death Channel (mortes do servidor), Guild, Party, Trade e conversas privadas.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ChatChannel, ChatMessage } from '../../../../shared/src/game/chat';
import { CHAT_MAX } from '../../../../shared/src/game/chat';

const IMG = '/assets/interface/images';

/** canais fixos + conversas privadas ('pm:<nome>') */
export type Channel = 'global' | 'server' | 'loot' | 'death' | 'guild' | 'party' | 'trade' | `pm:${string}`;
export interface ChatLine { at: Date; channel: Channel; text: string; color?: string; from?: string; level?: number; staff?: boolean }

const TABS: Array<[Channel, string]> = [
  ['global', 'Global'], ['server', 'Server Log'], ['loot', 'Loot'], ['death', 'Death Channel'], ['guild', 'Guild'], ['party', 'Party'], ['trade', 'Trade'],
];
/** cor de cada canal (como no Tibia: amarelo no chat aberto, azul nas privadas) */
export const CHAT_COLOR: Record<ChatChannel, string> = { global: '#f0f000', trade: '#ffa64d', guild: '#5fe05f', party: '#80c8ff', pm: '#9fb4ff' };
const TALK: Partial<Record<Channel, ChatChannel>> = { global: 'global', trade: 'trade', guild: 'guild', party: 'party' };

/** Converte uma mensagem do servidor numa linha do console (no canal certo, do ponto de vista de quem recebe). */
export function chatLine(m: ChatMessage, me: string): ChatLine {
  const other = m.ch === 'pm' ? (m.from.toLowerCase() === me.toLowerCase() ? m.to! : m.from) : '';
  return { at: new Date(m.at), channel: m.ch === 'pm' ? `pm:${other}` : m.ch, text: m.text, from: m.from, level: m.level, color: m.staff ? '#ff7a3c' : CHAT_COLOR[m.ch], ...(m.staff ? { staff: true } : {}) };
}

export function Console(props: { lines: ChatLine[]; me: string; onSend: (ch: ChatChannel, text: string, to?: string) => void }) {
  const [tab, setTab] = useState<Channel>('global');
  const [text, setText] = useState('');
  const [pms, setPms] = useState<string[]>([]);
  const [seen, setSeen] = useState<Record<string, number>>({});
  /** conversas fechadas: quantas linhas tinham ao fechar (voltam com mensagem nova) */
  const [closed, setClosed] = useState<Record<string, number>>({});
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  // conversas privadas abertas: as que recebem mensagem abrem sozinhas
  const pmNames = [...new Set([...pms, ...props.lines.filter((l) => l.channel.startsWith('pm:')).map((l) => l.channel.slice(3))])];
  const tabs: Array<[Channel, string]> = [...TABS, ...pmNames.map((n): [Channel, string] => [`pm:${n}`, n])];
  const count = (c: Channel) => props.lines.reduce((n, l) => n + (l.channel === c ? 1 : 0), 0);
  const shown = props.lines.filter((l) => l.channel === tab);
  useEffect(() => { box.current?.scrollTo({ top: box.current.scrollHeight }); setSeen((s) => ({ ...s, [tab]: shown.length })); }, [shown.length, tab]);

  const openPm = (name: string) => {
    if (name.toLowerCase() === props.me.toLowerCase()) return;
    const known = pmNames.find((x) => x.toLowerCase() === name.toLowerCase()) ?? name;
    setPms((p) => (p.includes(known) ? p : [...p, known]));
    setClosed((c) => { const n = { ...c }; delete n[`pm:${known}`]; return n; });
    setTab(`pm:${known}`); input.current?.focus();
  };
  const closeTab = () => {
    if (!tab.startsWith('pm:')) return;
    const n = tab.slice(3);
    setPms((p) => p.filter((x) => x !== n));
    setSeen((s) => ({ ...s, [tab]: count(tab) }));
    setClosed((c) => ({ ...c, [tab]: count(tab) }));
    setTab('global');
  };
  const send = (raw: string) => {
    // /pm Nome, texto (ou /w) abre a conversa privada direto do Global
    const cmd = /^\/(?:pm|w|msg)\s+([^,]+),\s*(.+)$/i.exec(raw);
    if (cmd) { openPm(cmd[1]!.trim()); return props.onSend('pm', cmd[2]!, cmd[1]!.trim()); }
    if (tab.startsWith('pm:')) return props.onSend('pm', raw, tab.slice(3));
    const ch = TALK[tab];
    if (ch) props.onSend(ch, raw);
  };
  const canTalk = !!TALK[tab] || tab.startsWith('pm:');
  // conversas privadas fechadas só voltam a aparecer com mensagem nova
  const visible = tabs.filter(([id]) => !id.startsWith('pm:') || tab === id || !(id in closed) || count(id) > closed[id]!);

  return (
    <div class="console">
      <div class="con-tabs">
        <img src={`${IMG}/console-buttonleft-up.png`} alt="" />
        {visible.map(([id, label]) => {
          const unread = id !== tab && count(id) > (seen[id] ?? 0) && id !== 'loot';
          return <button key={id} class={'con-tab' + (tab === id ? ' on' : '') + (unread ? ' unread' : '') + (id.startsWith('pm:') ? ' pm' : '')} onClick={() => setTab(id)}>{label}</button>;
        })}
        <img src={`${IMG}/console-buttonright-up.png`} alt="" />
        <span class="gap" />
        <img src={`${IMG}/console-buttonclose-up.png`} alt="" title={tab.startsWith('pm:') ? 'Fechar conversa' : 'Só as conversas privadas podem ser fechadas'}
          style={{ cursor: tab.startsWith('pm:') ? 'pointer' : 'default', opacity: tab.startsWith('pm:') ? 1 : 0.4 }} onClick={closeTab} />
      </div>
      <div class="con-lines" ref={box}>
        {shown.length === 0 && <div class="con-empty">{
          tab === 'global' ? 'Chat global: todos os jogadores online leem o que você escreve aqui. Clique no nome de alguém para mandar uma mensagem privada.'
            : tab === 'trade' ? 'Compra e venda entre jogadores, uma mensagem por minuto. Nunca negocie por dinheiro real.'
            : tab === 'guild' ? 'Chat da sua guild (só os membros online leem).'
            : tab === 'party' ? 'Chat da sua party (só os membros leem).'
            : tab.startsWith('pm:') ? `Conversa privada com ${tab.slice(3)}.` : ''}</div>}
        {shown.map((l, i) => (
          <div key={i} style={{ color: l.color ?? (l.channel === 'server' ? '#f0f0f0' : '#f0f000') }}>
            {l.at.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}{' '}
            {l.from ? (
              <>
                {l.staff && <span class="con-staff" title="Administrador do Baiak: Noxara">[ADM] </span>}
                <span class="con-name" title={l.from === props.me ? '' : `Mensagem privada para ${l.from}`} onClick={() => openPm(l.from!)}>{l.from}</span>
                {l.level ? ` [${l.level}]` : ''}: {l.text}
              </>
            ) : l.text}
          </div>
        ))}
      </div>
      <form class="con-input" onSubmit={(e) => { e.preventDefault(); if (text.trim()) { send(text.trim()); setText(''); } }}>
        <input ref={input} value={text} maxLength={CHAT_MAX} disabled={!canTalk}
          placeholder={canTalk ? (tab.startsWith('pm:') ? `Mensagem para ${tab.slice(3)}` : `Escreva no ${tabs.find(([id]) => id === tab)?.[1]} (Enter envia)`) : 'Este canal é só de leitura'}
          onInput={(e) => setText((e.target as HTMLInputElement).value)} aria-label="Mensagem" />
      </form>
    </div>
  );
}
